/**
 * `React.lazy`, hardened against the one failure it actually has in this app:
 * a stale client importing a chunk the current deploy no longer serves.
 *
 * THE INCIDENT THIS EXISTS FOR (2026-08-20). Every view is a lazy chunk with a
 * content-hashed name. A client that loaded `index.html` before a deploy keeps
 * asking for the OLD names on its next navigation — an open tab mid-deploy, a
 * webview's HTTP cache, a half-updated service worker. The deploy used to
 * delete the old chunks, the import rejected, and with no error boundary React
 * unmounted the entire tree: users reported wallet setup "not working at all"
 * and a manual create that "shows an empty screen". Both were this.
 *
 * THE RECOVERY IS A RELOAD, because a failed chunk import is self-healing by
 * definition: the fresh `index.html` (served no-store) names chunks that DO
 * exist. Reloading loses nothing here — every lazy view in this app is a
 * route, so the URL carries the state, and the reload lands the user exactly
 * where they were heading.
 *
 * ONCE, not in a loop. The marker is per-tab (sessionStorage) and cleared by
 * the next successful import, so one genuine outage cannot trap a user in a
 * reload cycle: the second failure falls through to the error boundary, which
 * shows a real message instead of a blank screen.
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const RELOADED_KEY = 'pmw.chunk-reloaded';

/** sessionStorage, tolerating environments where touching it throws. */
function marker(): string | null {
  try {
    return sessionStorage.getItem(RELOADED_KEY);
  } catch {
    return null;
  }
}

function setMarker(value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(RELOADED_KEY);
    else sessionStorage.setItem(RELOADED_KEY, value);
  } catch {
    // Nothing to do: without the marker the worst case is that a genuine
    // outage reloads more than once before the boundary catches it.
  }
}

/**
 * A lazy component whose chunk failure reloads the page once before failing.
 *
 * @param importer The dynamic import, exactly as passed to `React.lazy`.
 * @returns The lazy component.
 */
export function lazyWithReload<T extends ComponentType<unknown>>(
  importer: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await importer();
      // A successful import proves this client and the deploy agree again, so
      // the next mismatch (a future deploy) gets its own single reload.
      setMarker(null);
      return mod;
    } catch (error) {
      if (marker() === null) {
        setMarker('1');
        window.location.reload();
        // The reload is already in flight; never resolve, so React keeps the
        // Suspense fallback up instead of flashing an error first.
        return new Promise<never>(() => {});
      }
      throw error;
    }
  });
}
